import { useState, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import api from "@/lib/api";
import { PageHeader, TableShell, Loading, EmptyState } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { formatDate } from "@/lib/format";
import { Download, CalendarDays, X } from "lucide-react";

export default function PeriodCalendarView() {
  const { data = [], isLoading } = useQuery({ queryKey: ["calendar"], queryFn: () => api.get("/calendar").then((r) => r.data) });

  const [fy, setFy] = useState("all");
  const [quarter, setQuarter] = useState("all");
  const [period, setPeriod] = useState("all");
  const [month, setMonth] = useState("all");
  const [weekNo, setWeekNo] = useState("all");

  const scopeBy = (opts) =>
    data.filter((d) => {
      if (opts.fy && fy !== "all" && d.fy_label !== fy) return false;
      if (opts.quarter && quarter !== "all" && d.quarter_label !== quarter) return false;
      if (opts.period && period !== "all" && d.period_label !== period) return false;
      if (opts.month && month !== "all" && d.month_label !== month) return false;
      return true;
    });

  // ---- Dynamic (cascading) filter option lists ----
  const fys = useMemo(() => [...new Set(data.map((d) => d.fy_label))], [data]);
  const quarters = useMemo(() => [...new Set(scopeBy({ fy: true }).map((d) => d.quarter_label))].sort(), [data, fy]);
  const periods = useMemo(() => [...new Set(scopeBy({ fy: true, quarter: true }).map((d) => d.period_label))], [data, fy, quarter]);
  const months = useMemo(() => [...new Set(scopeBy({ fy: true, quarter: true, period: true }).map((d) => d.month_label))], [data, fy, quarter, period]);
  const weekNos = useMemo(
    () => [...new Set(scopeBy({ fy: true, quarter: true, period: true, month: true }).map((d) => d.week_no))].sort((a, b) => a - b),
    [data, fy, quarter, period, month]
  );

  const filtered = useMemo(
    () => scopeBy({ fy: true, quarter: true, period: true, month: true }).filter((d) => weekNo === "all" || String(d.week_no) === String(weekNo)),
    [data, fy, quarter, period, month, weekNo]
  );

  // reset downstream filters when an upstream filter changes
  const onFy = (v) => { setFy(v); setQuarter("all"); setPeriod("all"); setMonth("all"); setWeekNo("all"); };
  const onQuarter = (v) => { setQuarter(v); setPeriod("all"); setMonth("all"); setWeekNo("all"); };
  const onPeriod = (v) => { setPeriod(v); setMonth("all"); setWeekNo("all"); };
  const onMonth = (v) => { setMonth(v); setWeekNo("all"); };
  const clearAll = () => { setFy("all"); setQuarter("all"); setPeriod("all"); setMonth("all"); setWeekNo("all"); };
  const hasFilters = fy !== "all" || quarter !== "all" || period !== "all" || month !== "all" || weekNo !== "all";

  const exportCsv = () => {
    const header = ["FY", "Quarter", "Period", "Month", "Week No", "Week Commencing (Mon)", "Week Ending (Sun)"];
    const rows = filtered.map((d) => [d.fy_label, d.quarter_label, d.period_label, d.month_label, d.week_no, formatDate(d.wc_monday), formatDate(d.we_sunday)].join(","));
    const csv = [header.join(","), ...rows].join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "epgroup-period-calendar.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  const FilterBox = ({ label, value, onChange, testid, children }) => (
    <div>
      <label className="text-[11px] font-semibold uppercase tracking-wide text-slate-400">{label}</label>
      <Select value={String(value)} onValueChange={onChange}>
        <SelectTrigger className="mt-1" data-testid={testid}><SelectValue /></SelectTrigger>
        <SelectContent className="max-h-64">{children}</SelectContent>
      </Select>
    </div>
  );

  return (
    <div>
      <PageHeader title="Period Calendar" subtitle="4-4-5 financial calendar, FY27 – FY31. Week commencing Monday to week ending Sunday.">
        <Button variant="outline" onClick={exportCsv} data-testid="calendar-export-button"><Download className="w-4 h-4 mr-1.5" /> Export</Button>
      </PageHeader>

      <div className="grid grid-cols-2 lg:grid-cols-6 gap-3 mb-4">
        <FilterBox label="Financial Year" value={fy} onChange={onFy} testid="calendar-year-select">
          <SelectItem value="all">All years</SelectItem>
          {fys.map((f) => <SelectItem key={f} value={f}>{f}</SelectItem>)}
        </FilterBox>
        <FilterBox label="Quarter" value={quarter} onChange={onQuarter} testid="calendar-quarter-select">
          <SelectItem value="all">All quarters</SelectItem>
          {quarters.map((q) => <SelectItem key={q} value={q}>{q}</SelectItem>)}
        </FilterBox>
        <FilterBox label="Period" value={period} onChange={onPeriod} testid="calendar-period-select">
          <SelectItem value="all">All periods</SelectItem>
          {periods.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}
        </FilterBox>
        <FilterBox label="Month" value={month} onChange={onMonth} testid="calendar-month-select">
          <SelectItem value="all">All months</SelectItem>
          {months.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}
        </FilterBox>
        <FilterBox label="Week No." value={weekNo} onChange={setWeekNo} testid="calendar-weekno-select">
          <SelectItem value="all">All weeks</SelectItem>
          {weekNos.map((w) => <SelectItem key={w} value={String(w)}>Week {w}</SelectItem>)}
        </FilterBox>
        <div className="flex items-end">
          {hasFilters && (
            <Button variant="ghost" onClick={clearAll} className="text-slate-500" data-testid="calendar-clear-filters"><X className="w-4 h-4 mr-1" /> Clear</Button>
          )}
        </div>
      </div>

      {isLoading ? <Loading /> : (
        <TableShell>
          <table className="w-full text-sm" data-testid="period-calendar-table">
            <thead>
              <tr className="bg-slate-100 dark:bg-slate-800/80 text-left text-xs font-semibold uppercase tracking-wider text-slate-600 dark:text-slate-400">
                <th className="px-4 py-3">FY</th>
                <th className="px-4 py-3">Quarter</th>
                <th className="px-4 py-3">Period</th>
                <th className="px-4 py-3">Month</th>
                <th className="px-4 py-3 text-center">Week No.</th>
                <th className="px-4 py-3">W/C (Monday)</th>
                <th className="px-4 py-3">W/E (Sunday)</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
              {filtered.map((d) => (
                <tr key={`${d.fy}-${d.week_no}`} className="hover:bg-slate-50 dark:hover:bg-slate-800/40" data-testid={`calendar-row-${d.fy}-${d.week_no}`}>
                  <td className="px-4 py-2.5"><Badge variant="outline" className="font-mono-num bg-blue-50 text-blue-700 border-blue-200">{d.fy_label}</Badge></td>
                  <td className="px-4 py-2.5 text-slate-600 dark:text-slate-300">{d.quarter_label}</td>
                  <td className="px-4 py-2.5 font-medium text-slate-800 dark:text-slate-200">{d.period_label}</td>
                  <td className="px-4 py-2.5"><Badge variant="outline" className="font-mono-num bg-slate-50 text-slate-600 border-slate-200">{d.month_label}</Badge></td>
                  <td className="px-4 py-2.5 text-center font-mono-num text-slate-600">{d.week_no}</td>
                  <td className="px-4 py-2.5 font-mono-num text-slate-700 dark:text-slate-200">{formatDate(d.wc_monday)}</td>
                  <td className="px-4 py-2.5 font-mono-num text-slate-700 dark:text-slate-200">{formatDate(d.we_sunday)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {filtered.length === 0 && <EmptyState message="No weeks match your filters." />}
        </TableShell>
      )}
      <p className="mt-3 text-xs text-slate-400 flex items-center gap-1.5"><CalendarDays className="w-3.5 h-3.5" /> Showing {filtered.length} weeks. Dates in DD-MM-YYYY.</p>
    </div>
  );
}
